import React from 'react';
import styled from 'styled-components';
import { getSplitText } from '~/shared/utils';
import { Tooltip } from '../../Tooltip';
import { font, ChartCell, ChartCellWrapper } from './sharedStyles';
import { getColumnBorderFlags } from './columnBorderFlags';
import type { CellTextAlign } from '../cellAlignment';

const ButtonLabel = styled.span`
  cursor: pointer;
  display: block;
  width: 100%;
`;

const ButtonCellWrapper = styled.div<{ $align: CellTextAlign }>`
  cursor: pointer;
  position: absolute;
  top: 5px;
  left: 0;
  right: 0;
  width: 100%;
  text-align: ${({ $align }) => $align};
  /* Name/index column already has 12px left padding via TableWrapper; don't double it. */
  ${font}
`;

const TooltipHeader = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--search-text-color);
`;

const TooltipDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: var(--search-text-color);
  padding-bottom: 5px;
`;

export const ButtonRenderer =
  (
    searchValue: any,
    firstColumnIds: Set<string>,
    lastColumnIds: Set<string>,
    columnIds: string[],
    hasScroll: boolean | undefined,
    onRowClick?: (data: any) => void,
    textAlign: CellTextAlign = 'left',
  ) =>
  // eslint-disable-next-line react/display-name
  ({ cellData, rowData, columnIndex }: any) => {
    const { isFirst, isLast } = getColumnBorderFlags(columnIds, columnIndex, firstColumnIds, lastColumnIds, hasScroll);
    // cellData isn't always a string (e.g. a numeric column mistakenly typed as 'button'); guard against that.
    const cellText = cellData === null || cellData === undefined ? '' : String(cellData);
    const isSearchResult = cellText.toLowerCase().includes(String(searchValue ?? '').toLowerCase());

    return (
      <ChartCell $isfirst={isFirst ? 1 : 0} $islast={isLast ? 1 : 0}>
        <ChartCellWrapper>
          <ButtonCellWrapper
            $align={textAlign}
            onClick={event => {
              if (!onRowClick) return;
              event.stopPropagation();
              onRowClick({ event, rowData, cellData });
            }}
          >
            <Tooltip
              arrow
              html={
                <div>
                  <TooltipHeader>{cellData}</TooltipHeader>
                  {rowData.description && cellData !== rowData.description && (
                    <TooltipDescription>{rowData.description}</TooltipDescription>
                  )}
                </div>
              }
            >
              <ButtonLabel>{isSearchResult ? getSplitText(cellText, searchValue) : cellData}</ButtonLabel>
            </Tooltip>
          </ButtonCellWrapper>
        </ChartCellWrapper>
      </ChartCell>
    );
  };
